import { getGeeUrl } from '../api/getMapUrl';
import { frameKey } from './forecastFrames';

export type UrlEntry =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string; expiresAt: number }
  | { status: 'error'; message: string; attempts: number; expiresAt: number };

// (imageId, bands) → { band: url } を返す. 失敗時は null.
export type BandUrlFetcher = (imageId: string, bands: number[]) => Promise<Record<number, string> | null>;

// バックエンドは URL を最大600秒キャッシュして返すので、受け取ってからさらに長く使わないよう短めにする.
export const FRAME_URL_TTL_MS = 5 * 60 * 1000;
// 失敗もこの時間がたったら未取得に戻す. ストアはページを離れても残るので、失敗したままにしないため.
export const FRAME_ERROR_TTL_MS = 30 * 1000;

const IDLE: UrlEntry = { status: 'idle' };

const defaultFetcher: BandUrlFetcher = async (imageId, bands) => {
  const result = await getGeeUrl({ imageId, timeList: bands });
  return result?.map_url ?? null;
};

// Asset・バンドごとのタイル URL を保持する. 応答は「要求したキー」にだけ保存し、
// どの時刻を表示するかは購読側が現在の選択キーで決める（遅れて届いた応答で表示を上書きしない）.
export class FrameUrlStore {
  private entries = new Map<string, UrlEntry>();
  private listeners = new Set<() => void>();
  private version = 0;

  constructor(
    private fetcher: BandUrlFetcher = defaultFetcher,
    private now: () => number = Date.now,
    private ttlMs: number = FRAME_URL_TTL_MS,
    private errorTtlMs: number = FRAME_ERROR_TTL_MS,
  ) {}

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getVersion = () => this.version;

  // 期限切れの URL・失敗は未取得として扱う. 失敗回数も数え直しになる.
  getEntry(key: string): UrlEntry {
    const entry = this.entries.get(key);
    if (!entry) {
      return IDLE;
    }
    if ((entry.status === 'ready' || entry.status === 'error') && entry.expiresAt <= this.now()) {
      return IDLE;
    }
    return entry;
  }

  // 取得中・取得済みのバンドは除いて要求する. force のときだけ失敗済みのバンドも取り直す.
  async fetchBands(imageId: string, bands: number[], { force = false } = {}): Promise<void> {
    const targets = bands.filter(band => {
      const entry = this.getEntry(frameKey(imageId, band));
      if (entry.status === 'idle') return true;
      if (entry.status === 'error') return force;
      return false;
    });
    if (targets.length < 1) {
      return;
    }

    const previousAttempts = new Map<number, number>();
    targets.forEach(band => {
      const key = frameKey(imageId, band);
      const entry = this.getEntry(key);
      previousAttempts.set(band, entry.status === 'error' ? entry.attempts : 0);
      this.entries.set(key, { status: 'loading' });
    });
    this.emit();

    let urls: Record<number, string> | null = null;
    try {
      urls = await this.fetcher(imageId, targets);
    } catch (error) {
      urls = null;
    }

    const now = this.now();
    targets.forEach(band => {
      const key = frameKey(imageId, band);
      const url = urls?.[band];
      // HTTP 200 でも要求したバンドが無ければ、その時刻は ready にしない.
      if (typeof url === 'string' && url) {
        this.entries.set(key, { status: 'ready', url, expiresAt: now + this.ttlMs });
      } else {
        this.entries.set(key, {
          status: 'error',
          message: 'タイル URL を取得できませんでした',
          attempts: (previousAttempts.get(band) ?? 0) + 1,
          expiresAt: now + this.errorTtlMs,
        });
      }
    });
    this.emit();
  }

  private emit() {
    this.version += 1;
    this.listeners.forEach(listener => listener());
  }
}

export const frameUrlStore = new FrameUrlStore();
